import React, { useState, useEffect, useRef } from 'react';
import DashboardLayout from '../../layouts/DashboardLayout';
import { api, useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Progress } from '../../components/ui/progress';
import { Volume2, Mic, MicOff, ChevronLeft, ChevronRight, Loader2, Send, Check, X, ArrowRight, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const StudentSTTPage = () => {
  const { user } = useAuth();
  const [tests, setTests] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [speaking, setSpeaking] = useState(false);
  const [recording, setRecording] = useState(false);
  const [answer, setAnswer] = useState('');
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [selectedTestId, setSelectedTestId] = useState(null);
  const recognitionRef = useRef(null);
  const voiceSettingsRef = useRef({
    voice_name: 'p225',
    voice_speed: 1.0,
    tts_mode: 'coqui',
  });
  const audioRef = useRef(null);

  useEffect(() => {
    fetchTests();
    initSpeechRecognition();
    loadVoiceSettings();
    return () => {
      if (recognitionRef.current) recognitionRef.current.stop();
      if (audioRef.current) audioRef.current.pause();
      window.speechSynthesis?.cancel();
    };
  }, []);

  const loadVoiceSettings = async () => {
    try {
      const res = await api.get('/voice-settings');
      voiceSettingsRef.current = res.data;
    } catch (err) {
      // Use defaults silently
    }
  };

  const initSpeechRecognition = () => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map(result => result[0].transcript)
          .join('');
        setAnswer(transcript);
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setRecording(false);
        if (event.error === 'not-allowed') {
          toast.error('Microphone access denied. Please allow microphone access.');
        }
      };

      recognitionRef.current.onend = () => {
        setRecording(false);
      };
    }
  };

  const fetchTests = async () => {
    try {
      const res = await api.get(`/tests?module_type=stt`);
      setTests(res.data);
    } catch (err) {
      toast.error('Failed to load tests');
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestions = async (testId) => {
    try {
      const res = await api.get(`/tests/${testId}`);
      setQuestions(res.data.questions || []);
      setSelectedTestId(testId);
      setCurrentIndex(0);
    } catch (err) {
      toast.error('Failed to load questions');
    }
  };

  const playAudio = async () => {
    if (!questions[currentIndex]) return;

    // Stop any currently playing audio
    if (audioRef.current) {
      audioRef.current.pause();
      if (audioRef.current._blobUrl) URL.revokeObjectURL(audioRef.current._blobUrl);
      audioRef.current = null;
    }
    window.speechSynthesis?.cancel();

    const text    = questions[currentIndex].question_text;
    const speed   = Math.max(0.5, Math.min(2.0, voiceSettingsRef.current.voice_speed || 1.0));
    const ttsMode = voiceSettingsRef.current.tts_mode || 'coqui';

    setSpeaking(true);

    // ── Browser TTS (Normal mode) ────────────────────────────────────────
    if (ttsMode === 'browser') {
      if (!('speechSynthesis' in window)) {
        setSpeaking(false);
        toast.error('Text-to-Speech not supported in this browser');
        return;
      }
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate   = speed;
      utterance.onend  = () => setSpeaking(false);
      utterance.onerror = () => { setSpeaking(false); toast.error('Could not play audio'); };
      window.speechSynthesis.speak(utterance);
      return;
    }

    // ── Coqui AI TTS (AI Based mode) ────────────────────────────────────
    const speaker = voiceSettingsRef.current.voice_name || 'p225';
    let blobUrl = null;
    try {
      const response = await api.get('/tts/audio', {
        params: { text, speaker },
        responseType: 'blob',
      });
      blobUrl = URL.createObjectURL(response.data);
      const audio = new Audio(blobUrl);
      audio._blobUrl = blobUrl;
      audio.playbackRate = speed;
      audioRef.current = audio;
      audio.onended = () => { setSpeaking(false); URL.revokeObjectURL(blobUrl); audioRef.current = null; };
      audio.onerror = () => { setSpeaking(false); URL.revokeObjectURL(blobUrl); audioRef.current = null; toast.error('Could not play audio'); };
      await audio.play();
    } catch (err) {
      setSpeaking(false);
      if (blobUrl) URL.revokeObjectURL(blobUrl);
      audioRef.current = null;
      toast.error('Audio playback failed');
    }
  };

  const toggleRecording = () => {
    if (!recognitionRef.current) {
      toast.error('Speech recognition not supported in this browser');
      return;
    }

    if (recording) {
      recognitionRef.current.stop();
    } else {
      setAnswer('');
      recognitionRef.current.start();
      setRecording(true);
    }
  };

  const handleSubmit = async () => {
    const question = questions[currentIndex];
    if (!question || !answer.trim()) {
      toast.error('Please enter or speak your answer');
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.post('/attempts', {
        question_id: question.id,
        answer: answer.trim(),
        time_taken: 0
      });
      
      setResult({
        score: res.data.score,
        correctAnswer: res.data.correct_answer
      });
    } catch (err) {
      toast.error('Failed to submit answer');
    } finally {
      setSubmitting(false);
    }
  };

  const goToNext = () => {
    setAnswer('');
    setResult(null);
    setCurrentIndex(prev => prev + 1);
  };

  const currentQuestion = questions[currentIndex];
  const progress = questions.length > 0 ? ((currentIndex + 1) / questions.length) * 100 : 0;
  const hasSpeechRecognition = 'webkitSpeechRecognition' in window || 'SpeechRecognition' in window;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        </div>
      </DashboardLayout>
    );
  }

  // Show test cards if no test is selected
  if (!selectedTestId) {
    if (tests.length === 0) {
      return (
        <DashboardLayout>
          <div className="max-w-2xl mx-auto text-center py-12">
            <Mic className="h-16 w-16 mx-auto text-slate-300 mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
              No STT Tests Available
            </h2>
            <p className="text-slate-500">
              Your teacher hasn't assigned any speech-to-text tests yet.
            </p>
          </div>
        </DashboardLayout>
      );
    }

    return (
      <DashboardLayout>
        <div className="space-y-6 animate-fade-in">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
              Speech to Text Tests
            </h1>
            <p className="text-slate-500">
              Select a test to start practicing
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {tests.map((test) => {
              const isLocked = !!test.is_locked;
              return (
              <Card key={test.id} className={`module-card h-full group transition-shadow ${isLocked ? 'opacity-75' : 'cursor-pointer hover:shadow-lg'}`} onClick={() => !isLocked && fetchQuestions(test.id)}>
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300">
                      {test.test_number}
                    </span>
                    {isLocked && (
                      <span className="flex items-center gap-1 px-2 py-0.5 text-xs font-bold rounded-full bg-amber-100 text-amber-700">
                        🔒 Locked
                      </span>
                    )}
                  </div>
                  <div className="inline-flex p-3 rounded-xl bg-sky-100 dark:bg-sky-900/30 mb-3 group-hover:scale-110 transition-transform">
                    <Mic className="h-6 w-6 text-sky-600 dark:text-sky-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">
                    {test.test_name}
                  </h3>
                  <p className="text-xs text-slate-500 mb-3">{test.question_count || 0} items</p>
                  {isLocked ? (
                    <p className="text-xs text-amber-600 mt-2">This test is locked. You cannot attempt it.</p>
                  ) : (
                    <>
                      <div className="flex items-center justify-between text-sm mt-2">
                        <span className="text-slate-500">Attempts: {test.attempts || 0}</span>
                        <span className={`font-semibold ${(test.best_score || 0) >= 80 ? 'text-emerald-600' : (test.best_score || 0) >= 50 ? 'text-amber-600' : 'text-slate-600'}`}>
                          Best: {test.best_score || 0}%
                        </span>
                      </div>
                      <div className="mt-4 flex items-center text-sky-600 dark:text-sky-400 text-sm font-medium group-hover:translate-x-1 transition-transform">
                        Start Practice <ArrowRight className="h-4 w-4 ml-1" />
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
              );
            })}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (questions.length === 0) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto text-center py-12">
          <Mic className="h-16 w-16 mx-auto text-slate-300 mb-4" />
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
            No Questions Available
          </h2>
          <p className="text-slate-500">
            Failed to load questions for this test.
          </p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => {
              setSelectedTestId(null);
              setQuestions([]);
              setCurrentIndex(0);
              setAnswer('');
              setResult(null);
            }}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Tests
          </Button>
        </div>
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
            Speech to Text Practice
          </h1>
          <p className="text-slate-500">
            Listen to the audio and type or speak what you hear
          </p>
        </div>

        {/* Progress */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm text-slate-500">
            <span>Progress</span>
            <span>{currentIndex + 1} of {questions.length}</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Question Card */}
        <Card className="overflow-hidden">
          <div className="bg-gradient-to-r from-sky-500 to-indigo-600 p-6">
            <div className="flex items-center justify-center gap-4">
              <Button
                size="lg"
                variant="secondary"
                onClick={playAudio}
                disabled={speaking}
                className="h-16 w-16 rounded-full"
                data-testid="play-audio-btn"
              >
                <Volume2 className={`h-8 w-8 ${speaking ? 'animate-pulse' : ''}`} />
              </Button>
              <span className="text-white text-lg font-medium">
                {speaking ? 'Playing...' : 'Click to listen'}
              </span>
            </div>
          </div>
          <CardContent className="p-8 space-y-6">
            {!result ? (
              <>
                {/* Input Area */}
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <Textarea
                      value={answer}
                      onChange={(e) => setAnswer(e.target.value)}
                      placeholder="Type what you hear..."
                      rows={4}
                      className="speech-input flex-1"
                      data-testid="stt-answer-input"
                    />
                    {hasSpeechRecognition && (
                      <Button
                        size="lg"
                        variant={recording ? 'destructive' : 'outline'}
                        onClick={toggleRecording}
                        className="h-20 w-20 rounded-full flex-shrink-0"
                        data-testid="record-btn"
                      >
                        {recording ? (
                          <MicOff className="h-8 w-8 recording-indicator" />
                        ) : (
                          <Mic className="h-8 w-8" />
                        )}
                      </Button>
                    )}
                  </div>
                  {hasSpeechRecognition && (
                    <p className="text-sm text-slate-500 text-center">
                      {recording ? 'Listening... Speak now!' : 'Or click the microphone to speak your answer'}
                    </p>
                  )}
                  <p className="text-xs text-slate-400 text-center italic">
                    Punctuation, extra spaces, and letter case are ignored during evaluation
                  </p>
                </div>

                {/* Submit */}
                <div className="flex justify-center">
                  <Button
                    onClick={handleSubmit}
                    disabled={submitting || !answer.trim()}
                    className="bg-sky-600 hover:bg-sky-700"
                    data-testid="submit-answer-btn"
                  >
                    {submitting ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Send className="h-4 w-4 mr-2" />
                    )}
                    Submit Answer
                  </Button>
                </div>
              </>
            ) : (
              /* Result */
              <div className="text-center space-y-6">
                <div className={`inline-flex p-4 rounded-full ${
                  result.score >= 80 ? 'bg-emerald-100' :
                  result.score >= 50 ? 'bg-amber-100' :
                  'bg-rose-100'
                }`}>
                  {result.score >= 80 ? (
                    <Check className="h-12 w-12 text-emerald-600" />
                  ) : (
                    <X className="h-12 w-12 text-rose-600" />
                  )}
                </div>
                
                <div>
                  <p className="text-4xl font-bold text-slate-900 dark:text-white mb-2">
                    {Math.round(result.score)}%
                  </p>
                  <p className={`text-lg ${
                    result.score >= 80 ? 'text-emerald-600' :
                    result.score >= 50 ? 'text-amber-600' :
                    'text-rose-600'
                  }`}>
                    {result.score >= 80 ? 'Excellent!' :
                     result.score >= 50 ? 'Good try!' :
                     'Keep practicing!'}
                  </p>
                </div>

                <div className="p-4 rounded-xl bg-slate-100 dark:bg-slate-800">
                  <p className="text-sm text-slate-500 mb-2">Correct Answer:</p>
                  <p className="text-lg text-slate-900 dark:text-white">
                    "{result.correctAnswer}"
                  </p>
                </div>

                <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-900">
                  <p className="text-sm text-slate-500 mb-2">Your Answer:</p>
                  <p className="text-lg text-slate-700 dark:text-slate-300">
                    "{answer}"
                  </p>
                </div>

                {currentIndex < questions.length - 1 && (
                  <Button onClick={goToNext} className="bg-indigo-600 hover:bg-indigo-700" data-testid="next-btn">
                    Next Question
                    <ChevronRight className="h-4 w-4 ml-2" />
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Navigation */}
        {!result && (
          <div className="flex justify-between">
            <Button
              variant="outline"
              onClick={() => {
                setAnswer('');
                setResult(null);
                setCurrentIndex(prev => prev - 1);
              }}
              disabled={currentIndex === 0}
              data-testid="prev-question-btn"
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              Previous
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setAnswer('');
                setResult(null);
                setCurrentIndex(prev => prev + 1);
              }}
              disabled={currentIndex === questions.length - 1}
              data-testid="skip-question-btn"
            >
              Skip
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default StudentSTTPage;
